import type { ReactNode, Ref } from 'react'
import cx from 'classnames'
import { Button } from '@/components/Button'
import { LoadingOverlay } from '@/components/LoadingOverlay'
import { useElementSize } from '@/hooks/element-hooks'
import { useLoading } from '@/hooks/loading-hooks'
import { isEmptyNode } from '@/utils/react-util'
import type { PricingInfo } from '@/features/jobs/drafting/quote-display'
import { Pricing } from '@/features/jobs/drafting/components/JobCreation/Submission/Pricing'
import styles from './Submission.module.css'

export interface SubmissionProps {
  shadow?: boolean
  isLoading: boolean
  shouldCollectPaymentInfo: boolean
  pricing?: PricingInfo | null
  billingSection?: ReactNode
  /**
   * Rendered next to billing when a coupon is selected.
   */
  couponSection?: ReactNode
  /**
   * Rendered after pricing when no coupon is selected yet.
   */
  emptyCouponSection?: ReactNode
  canSubmit: boolean
  isSubmitPending: boolean
  onSubmit: () => unknown
  isAllowedToSubmit: boolean
  statusModal?: ReactNode
  testId?: string
}

/**
 * When the box width is within this range, use the side-by-side layout.
 */
const SIDE_BY_SIDE_LAYOUT_BREAKPOINT = { min: 620, max: 750 }

export function Submission({
  shadow,
  isLoading,
  shouldCollectPaymentInfo,
  pricing,
  billingSection,
  couponSection,
  emptyCouponSection,
  canSubmit,
  isSubmitPending,
  onSubmit,
  isAllowedToSubmit,
  statusModal,
  testId,
}: SubmissionProps) {
  const loading = useLoading(isLoading)
  const { size: boxSize, connectRef } = useElementSize()
  const shouldShowPricing = !!pricing
  const sideBySide =
    shouldCollectPaymentInfo &&
    shouldShowPricing &&
    boxSize.width < SIDE_BY_SIDE_LAYOUT_BREAKPOINT.max &&
    boxSize.width > SIDE_BY_SIDE_LAYOUT_BREAKPOINT.min

  const pricingSection = <Pricing boxed={sideBySide} pricing={pricing} />

  return (
    <SubmissionBox
      shadow={shadow}
      containerRef={connectRef}
      testId={
        testId &&
        (loading ? `${testId}-submission-loading` : `${testId}-submission`)
      }
    >
      <Content>
        {sideBySide ? (
          <>
            <Column>
              {billingSection}
              {couponSection}
              {emptyCouponSection}
            </Column>
            <Column>{pricingSection}</Column>
          </>
        ) : (
          <Column>
            {billingSection}
            {couponSection}
            {pricingSection}
            {emptyCouponSection}
          </Column>
        )}
      </Content>
      <Spaced>
        <ConfirmButton
          canSubmit={canSubmit}
          isPending={isSubmitPending}
          onPress={onSubmit}
          testId={testId && `${testId}-submit-request-button`}
        />
        <Instructions isAllowedToSubmit={isAllowedToSubmit} />
      </Spaced>

      {loading && <LoadingOverlay />}
      {statusModal}
    </SubmissionBox>
  )
}

interface SubmissionBoxProps {
  children?: ReactNode
  shadow?: boolean
  containerRef: Ref<HTMLDivElement>
  testId?: string
}

function SubmissionBox({
  shadow,
  containerRef,
  children,
  testId,
}: SubmissionBoxProps) {
  return (
    <div
      className={cx(styles.root, shadow && styles.shadow)}
      data-test={testId}
      ref={containerRef}
    >
      {children}
    </div>
  )
}

interface ContentProps {
  children?: ReactNode
}

function Content({ children }: ContentProps) {
  return <div className={styles.content}>{children}</div>
}

interface ColumnProps {
  children?: ReactNode
}

function Column({ children }: ColumnProps) {
  return isEmptyNode(children) ? null : (
    <div className={styles.column}>{children}</div>
  )
}

interface ConfirmButtonProps {
  canSubmit: boolean
  isPending: boolean
  onPress: () => unknown
  testId?: string
}

function ConfirmButton({
  canSubmit,
  isPending,
  onPress,
  testId,
}: ConfirmButtonProps) {
  return (
    <Button
      label="Confirm Order"
      block
      variant="primary"
      size="cta"
      isDisabled={!canSubmit}
      isPending={isPending}
      onPress={onPress}
      testId={testId}
    />
  )
}

interface InstructionsProps {
  isAllowedToSubmit: boolean
}

function Instructions({ isAllowedToSubmit }: InstructionsProps) {
  return (
    <div className={styles.instructions}>
      {isAllowedToSubmit ? (
        <>
          Once you confirm your order and complete checkout, you will be
          redirected to your dashboard.
        </>
      ) : (
        <>
          Ready to submit but you are not allowed to do so. If you believe this
          is a mistake, please contact an administrator
        </>
      )}
    </div>
  )
}

interface SpacedProps {
  children?: ReactNode
}

function Spaced({ children }: SpacedProps) {
  return <div className={styles.spaced}>{children}</div>
}
